import React, { useState, useEffect } from 'react';
import { X, Search, Plus, Trash2, DollarSign, AlertCircle, CheckCircle, Receipt, Link2 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { Icon } from '@iconify/react';
import { searchExpenses } from '../services/walletService';
import api from '../services/api';

const MultiExpenseModal = ({ isOpen, onClose, onSuccess, movement }) => {
    const [step, setStep] = useState(1);
    const [loading, setLoading] = useState(false);

    // Búsqueda y selección de facturas (expenses)
    const [searchTerm, setSearchTerm] = useState('');
    const [availableExpenses, setAvailableExpenses] = useState([]);
    const [selectedExpenses, setSelectedExpenses] = useState([]);
    const [totalAllocated, setTotalAllocated] = useState(0);

    // Calcular el abs del movimiento
    const totalAmount = movement ? Math.abs(parseFloat(movement.valor || 0)) : 0;

    useEffect(() => {
        if (isOpen && step === 1) {
            doSearch('');
        }
    }, [isOpen, step, movement]);

    useEffect(() => {
        const total = selectedExpenses.reduce((sum, exp) => sum + parseFloat(exp.allocated_amount || 0), 0);
        setTotalAllocated(total);
    }, [selectedExpenses]);

    const doSearch = async (term = '') => {
        try {
            setLoading(true);
            const response = await searchExpenses(term, totalAmount);
            setAvailableExpenses(response.data || []);
        } catch (error) {
            console.error('Error fetching expenses:', error);
            toast.error('Error buscando facturas de compra');
            setAvailableExpenses([]);
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = (e) => {
        const value = e.target.value;
        setSearchTerm(value);
        if (value.length > 2) {
            doSearch(value);
        } else if (value.length === 0) {
            doSearch('');
        }
    };

    const addExpense = (expense) => {
        if (selectedExpenses.find(e => e.id === expense.id)) {
            toast.error('Gasto ya seleccionado');
            return;
        }

        // Sugerir el saldo restante o el total del gasto, lo que sea menor
        const remainingToAllocate = totalAmount - totalAllocated;
        const suggestedAmount = remainingToAllocate > 0 ? remainingToAllocate : 0;
        // Asumiendo que el gasto usualmente se cubre completo o parcial
        const expAmountStr = expense.amount ? Math.abs(parseFloat(expense.amount)) : 0;
        
        const allocate = Math.min(suggestedAmount, expAmountStr);

        const newExpense = {
            ...expense,
            allocated_amount: allocate || expAmountStr,
            rp_number: '', // RP Siigo
            notes: ''
        };

        setSelectedExpenses([...selectedExpenses, newExpense]);
        setSearchTerm('');
    };

    const removeExpense = (id) => {
        setSelectedExpenses(selectedExpenses.filter(e => e.id !== id));
    };

    const updateAllocation = (id, prop, value) => {
        setSelectedExpenses(selectedExpenses.map(e =>
            e.id === id ? { ...e, [prop]: prop === 'allocated_amount' ? (parseFloat(value) || 0) : value } : e
        ));
    };

    const handleSubmit = async () => {
        try {
            if (selectedExpenses.length === 0) {
                toast.error('Debe seleccionar al menos un gasto');
                return;
            }

            const diff = Math.abs(totalAmount - totalAllocated);
            if (diff > 100) {
                toast.error(`La suma de asignaciones ($${totalAllocated.toLocaleString()}) no cubre el valor del movimiento ($${totalAmount.toLocaleString()})`);
                return;
            }

            setLoading(true);

            const allocations = selectedExpenses.map(e => ({
                expense_id: e.id,
                allocated_amount: e.allocated_amount,
                rp_number: e.rp_number || null,
                notes: e.notes || null
            }));

            // Usar el nuevo endpoint directo
            await api.post(`/wallet/movements/${movement.id}/link-multiple-expenses`, {
                allocations
            });

            toast.success('Gastos cruzados exitosamente');
            if (onSuccess) onSuccess();
            handleClose();

        } catch (error) {
            console.error('Error guardando asignación:', error);
            toast.error(error.response?.data?.message || 'Error al guardar la asignación');
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setStep(1);
        setSelectedExpenses([]);
        setSearchTerm('');
        onClose();
    };

    const fmtPesos = (val) => `$${Math.round(val || 0).toLocaleString('es-CO')}`;

    if (!isOpen || !movement) return null;

    const remainingBalance = totalAmount - totalAllocated;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] flex flex-col">
                <div className="flex items-center justify-between p-6 border-b border-gray-200">
                    <div>
                        <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                            <Link2 className="w-6 h-6 text-orange-500" />
                            Pago Múltiple a Gastos
                        </h2>
                        <p className="text-sm text-gray-600 mt-1">
                            Distribuye la salida bancaria de {fmtPesos(totalAmount)} COP en varias Facturas
                        </p>
                    </div>
                    <button onClick={handleClose} className="text-gray-400 hover:text-gray-600">
                        <X className="w-6 h-6" />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto p-6 bg-gray-50/50">
                    {/* Resumen del Saldo */}
                    <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm mb-6 flex justify-between items-center">
                        <div className="flex items-center gap-3">
                            <div className="bg-red-100 p-2 rounded-lg text-red-600">
                                <DollarSign className="w-6 h-6" />
                            </div>
                            <div>
                                <p className="text-xs text-gray-500 font-medium">Movimiento</p>
                                <p className="text-sm font-semibold truncate max-w-xs" title={movement.descripcion}>{movement.descripcion}</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-8">
                            <div className="text-right">
                                <p className="text-xs text-gray-500">Monto del Banco</p>
                                <p className="text-lg font-bold text-gray-900">{fmtPesos(totalAmount)}</p>
                            </div>
                            <div className="text-right">
                                <p className="text-xs text-gray-500">Asignado</p>
                                <p className="text-lg font-bold text-orange-600">{fmtPesos(totalAllocated)}</p>
                            </div>
                            <div className="text-right">
                                <p className="text-xs text-gray-500">Diferencia</p>
                                <p className={`text-lg font-bold ${Math.abs(remainingBalance) > 100 ? 'text-red-500' : 'text-green-600'}`}>
                                    {fmtPesos(remainingBalance)}
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Selector (Izquierda) */}
                        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                            <h3 className="text-sm font-semibold text-gray-800 mb-3 flex items-center">
                                <Search className="w-4 h-4 mr-2 text-gray-400" />
                                Buscar Gastos (Facturas)
                            </h3>
                            <input
                                type="text"
                                placeholder="Proveedor, concepto o #FC..."
                                value={searchTerm}
                                onChange={handleSearch}
                                className="w-full px-3 py-2 border border-gray-300 rounded mb-3 text-sm focus:ring-2 focus:ring-orange-500"
                            />

                            <div className="max-h-80 overflow-y-auto space-y-2 pr-1">
                                {loading ? (
                                    <p className="text-sm text-gray-500 text-center py-4">Buscando...</p>
                                ) : availableExpenses.length === 0 ? (
                                    <p className="text-sm text-gray-500 text-center py-4">{searchTerm ? 'No hay resultados' : 'Escribe para buscar'}</p>
                                ) : (
                                    availableExpenses
                                        .filter(e => !selectedExpenses.find(s => s.id === e.id))
                                        .map(exp => (
                                            <div key={exp.id} className="p-3 border border-gray-100 rounded hover:border-orange-200 hover:bg-orange-50 transition-colors flex justify-between items-center group cursor-pointer" onClick={() => addExpense(exp)}>
                                                <div>
                                                    <p className="font-semibold text-sm text-gray-800 flex items-center gap-1">
                                                        <Receipt className="w-3 h-3 text-orange-400" />
                                                        {exp.provider_name || 'Sin Proveedor'}
                                                    </p>
                                                    <p className="text-xs text-gray-500 truncate max-w-[200px]">{exp.concept || 'Gasto genérico'}</p>
                                                    {exp.provider_invoice_number && <span className="text-[10px] text-gray-400">FC: {exp.provider_invoice_number}</span>}
                                                </div>
                                                <div className="text-right">
                                                    <p className="font-bold text-red-600 text-sm">{fmtPesos(exp.amount)}</p>
                                                    <button className="text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded mt-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                                        Añadir
                                                    </button>
                                                </div>
                                            </div>
                                        ))
                                )}
                            </div>
                        </div>

                        {/* Selección (Derecha) */}
                        <div className="bg-white p-4 rounded-lg shadow-sm border border-orange-200">
                            <h3 className="text-sm font-semibold text-gray-800 mb-3 flex items-center justify-between">
                                <span>Facturas a Pagar ({selectedExpenses.length})</span>
                                {Math.abs(remainingBalance) <= 100 && selectedExpenses.length > 0 && (
                                    <CheckCircle className="w-5 h-5 text-green-500" />
                                )}
                            </h3>
                            
                            {selectedExpenses.length === 0 ? (
                                <div className="h-40 flex items-center justify-center text-center text-sm text-gray-400 p-6 border-2 border-dashed border-gray-200 rounded">
                                    Selecciona gastos de la izquierda para distribuirlos.
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    {selectedExpenses.map(exp => (
                                        <div key={exp.id} className="p-3 border border-orange-100 bg-orange-50/50 rounded flex flex-col gap-2 relative">
                                            <button onClick={() => removeExpense(exp.id)} className="absolute top-2 right-2 text-red-400 hover:text-red-600">
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                            
                                            <div>
                                                <p className="font-semibold text-sm text-gray-800">{exp.provider_name}</p>
                                                <p className="text-xs text-gray-500">FC: {exp.provider_invoice_number || 'N/A'}</p>
                                            </div>
                                            
                                            <div className="flex gap-2">
                                                <div className="flex-1">
                                                    <label className="text-[10px] uppercase text-gray-500 font-medium">Abono (COP)</label>
                                                    <input 
                                                        type="number" 
                                                        value={exp.allocated_amount}
                                                        onChange={(e) => updateAllocation(exp.id, 'allocated_amount', e.target.value)}
                                                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded font-semibold text-orange-700"
                                                    />
                                                </div>
                                                <div className="flex-1">
                                                    <label className="text-[10px] uppercase text-blue-600 font-bold flex items-center gap-1 mb-0.5">
                                                        <Icon icon="mdi:flash" className="w-3 h-3"/> Recibo Siigo (RP)
                                                    </label>
                                                    <input 
                                                        type="text" 
                                                        placeholder="Auto-generado al vincular"
                                                        value={exp.rp_number}
                                                        onChange={(e) => updateAllocation(exp.id, 'rp_number', e.target.value.toUpperCase())}
                                                        className="w-full px-2 py-1 text-sm border border-blue-200 bg-blue-50/50 rounded text-blue-900 placeholder-blue-400 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className="p-4 border-t border-gray-200 flex justify-end gap-3 bg-white">
                    <button onClick={handleClose} className="px-4 py-2 border border-gray-300 text-gray-700 rounded shadow-sm hover:bg-gray-50">Cancelar</button>
                    <button 
                        onClick={handleSubmit} 
                        disabled={loading || selectedExpenses.length === 0 || Math.abs(remainingBalance) > 100}
                        className={`px-6 py-2 rounded shadow-sm font-medium transition-colors ${
                            loading || selectedExpenses.length === 0 || Math.abs(remainingBalance) > 100 
                            ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                            : 'bg-orange-500 text-white hover:bg-orange-600'
                        }`}
                    >
                        {loading ? 'Validando...' : 'Confirmar Cruce'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default MultiExpenseModal;
